import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  drawerStyle,
  tableRowSize,
  stickyHeader,
  defaultAllSelection,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { paymentBankHeader } from "pages/PayRoll/PayRollHeaders";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { CreatePaymentBank } from "./CreatePaymentBank";
import { ViewPaymentBank } from "./ViewPaymentBank";

export const PaymentBank = () => {
  const { domain, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paymentBankDetails, setPaymentBankDetails] = useState([]);
  const containerRef = useRef(null);
  const [individualBankData, setIndividualBankData] = useState([]);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Setup",
    "Payment Bank"
  );

  const {
    result: paymentBankData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}payroll/setup/paymentbank/payment-bank/?company_ids=[${
      parseInt(topbarCompanyId) === defaultAllSelection ? "" : topbarCompanyId
    }]`
  );
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [hanldeLoader, setHanldeLoader] = useState(false);
  const [getId, setGetId] = useState("");

  const hanldeCreatePanel = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewPanel = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (paymentBankData?.length !== 0 && paymentBankData && hanldeLoader) {
      const item = paymentBankData.find((d) => d.id === getId);
      setIndividualBankData(item);
    }
  }, [getId, hanldeLoader, paymentBankData]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setPaymentBankDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          hanldeCreatePanel();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create shift"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHanldeLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  return !hanldeLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Payment Bank
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a payrun cycle by defining the pay period and employee group.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Payment Bank Records"
            filterHeaders={paymentBankHeader}
            data={paymentBankData}
            filteredData={paymentBankDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={NewPayrunButton}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {paymentBankHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {paymentBankDetails?.length === 0 ? (
                    <InfoMessage dataLength={paymentBankData} />
                  ) : (
                    paymentBankDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {paymentBankHeader.map((head) => (
                            <Mui.TableCell
                              onClick={() => {
                                handleViewPanel();
                                setIndividualBankData(value);
                              }}
                            >
                              {value[head.key] ?? "N/A"}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {paymentBankDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={paymentBankDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <CreatePaymentBank
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setHanldeLoader={setHanldeLoader}
          />
        ) : (
          <ViewPaymentBank
            onClose={handleViewClosePanel}
            individualBankData={individualBankData}
            reCallApi={reCallApi}
            setGetId={setGetId}
            setHanldeLoader={setHanldeLoader}
            screenAccess={check_access}
          />
        )}
      </Drawer>
    </React.Fragment>
  );
};
